import _ from 'lodash';
import { sqTrendStore } from '@/core/core.stores';
import { getDefaultCompareViewPropertyValue } from '@/services/compareView.utilities';
import { COMMON_PROPS, type PROPS_TO_DEHYDRATE } from '@/trendData/baseItem.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { createItem, pruneDehydratedItemProps, shouldDehydrateItem } from '@/trendData/itemStore.utilities';
import { removeColor } from '@/trendData/itemStoreColor.utilities';
import {
  DASH_STYLES,
  ITEM_CHILDREN_TYPES,
  ITEM_TYPES,
  SAMPLE_OPTIONS,
  Y_AXIS_TYPES,
} from '@/trendData/trendData.constants';
import {
  createUniqueCapsuleSeriesId,
  deriveUncertainCapsuleSeriesRenames,
  getAlternateEndTime,
  getPreviewSeriesId,
} from '@/trendData/trendSeries.utilities';
import { getYAxisConfig, updateComputedYAxisExtremes } from '@/trendData/yAxis.actions';
import { getCapsuleDuration } from '@/utilities/utilities';
import type { SeriesRuntimeSample } from '@/trendData/seriesRuntime.types';
import type { Asset, CapsuleItem, CapsuleToAdd, SeriesItem, StringEnum } from '@/trendData/trendData.types';
import type { ParametersMap } from '@/utilities/formula.constants';

type SeriesInterpolation = string | { value?: string | null } | null | undefined;

/** Reference to the bound item `addShadedArea` stores on the child series */
export type ShadedAreaBound = {
  id: string;
  isSignal: boolean;
};

export type SeriesDataSample = SeriesRuntimeSample;

interface AddSeriesPayload {
  id: string;
  name: string;
  lane: number;
  alignment: string;
  color?: string;
  interpolationMethod?: SeriesInterpolation;
  isArchived?: boolean;
  effectivePermissions?: unknown;
  laneReservation?: string;
}

export type TrendCapsuleStoreItem = CapsuleToAdd & { selected?: boolean; duration?: number; id: string };

export type SeriesStoreItem = Omit<
  SeriesItem,
  | 'zones'
  | 'capsuleSegmentData'
  | 'samples'
  | 'data'
  | 'stringEnum'
  | 'assets'
  | 'dataStatus'
  | 'datasource'
  | 'description'
  | 'displayUnitOfMeasure'
> & {
  id: string;
  interestId?: string;
  capsuleId?: string;
  duration: number | undefined;
  startTime?: number | null;
  endTime?: number | null;
  otherChildrenOf?: Array<string | undefined>;
  childType?: string;
  idChildOf?: string;
  rightAxis?: boolean;
  showDataLabels?: boolean;
  column?: string;
  colorBy?: string;
  shadedAreaLower?: ShadedAreaBound | null;
  shadedAreaUpper?: ShadedAreaBound | null;
  assets: Asset[];
  dataStatus?: string;
  datasource?: { name: string };
  description?: string;
  displayUnitOfMeasure?: unknown;
  dirty?: boolean;
} & Record<string, unknown>;

type SeriesProps = Partial<SeriesStoreItem> & Record<string, unknown>;

type SeriesStoreItemDehydrated = Partial<
  Pick<SeriesStoreItem, Extract<(typeof PROPS_TO_DEHYDRATE)[number], keyof SeriesStoreItem>>
> & {
  id: string;
  name: string;
  dashStyle: string;
  lane: number;
  alignment?: string;
  interpolationMethod?: SeriesInterpolation;
};

interface AddSeriesFromCapsulePayload {
  capsule: TrendCapsuleStoreItem & { selected?: boolean };
  interest: SeriesStoreItem;
}

export interface AddDataPayload {
  id: string;
  samples: SeriesDataSample[];
  valueUnitOfMeasure?: string;
  warningCount?: number;
  warningLogs?: unknown[] | unknown;
  interpolationMethod?: string;
  stringEnum?: StringEnum[];
  capsuleSegmentSamples?: SeriesDataSample[];
  color?: string;
}

/** What a series fetch reports about the signal itself; its samples go to the series runtime store */
interface SeriesResultsPayload {
  id: string;
  valueUnitOfMeasure?: string;
  interpolationMethod?: string;
}

interface SignalSegmentsPayload extends SeriesResultsPayload {
  /** Intervals with their capsule series resolved by the dispatching action */
  requestCapsules: Array<{ capsuleSeriesId?: string }>;
}

interface PreviewSeriesDefinition {
  formula?: string;
  parameters?: ParametersMap;
  id?: string;
  color?: string | null;
  sampleDisplayOption?: string;
}

export interface PreviewSeriesPayload {
  formula?: string;
  parameters?: ParametersMap;
  id: string;
  name: string;
  samples: SeriesDataSample[];
  valueUnitOfMeasure?: string;
  lane: number;
  alignment: string;
  color?: string | null;
  lineWidth?: number;
  sampleDisplayOption?: string;
  interpolationMethod?: string;
  stringEnum?: StringEnum[];
  capsuleSegmentSamples?: SeriesDataSample[];
}

type TrendSeriesStoreState = {
  items: SeriesStoreItem[];
  previewChartItem: SeriesStoreItem | SeriesStoreItem[] | undefined;
  editingId?: string;
  previewSeriesDefinition?: PreviewSeriesDefinition | null;
  primarySeries: any; // any because of Baobab's `monkey()`
  itemsAndPreview: any; // any because of Baobab's `monkey()`
};

export type TrendSeriesDehydratedState = {
  items: SeriesStoreItemDehydrated[];
  editingId?: string | null;
  previewSeriesDefinition?: PreviewSeriesDefinition | null;
};

/**
 * A store for containing timeseries which can be displayed on the chart. A series is composed of an array of data
 * points and their associated timestamps.
 *
 * This store is augmented with additional functionality from BaseItemStore.
 */
export class TrendSeriesStore extends BaseItemStore<SeriesItem> {
  static readonly storeName = 'sqTrendSeriesStore';

  initialize(): void {
    const initialState: TrendSeriesStoreState = {
      ...COMMON_PROPS,
      // Avoid clearing state that is not dehydrated when the store is re-initialized. Async calls will repopulate
      // these properties as needed
      previewChartItem: this.state ? this.state.get('previewChartItem') : undefined,
      // all series that are not converted from capsules
      primarySeries: this.monkey(['items'], (items: SeriesStoreItem[] = []) => items.filter((item) => !item.isChildOf)),
      itemsAndPreview: this.monkey(
        ['items'],
        ['editingId'],
        ['previewSeriesDefinition'],
        ['previewChartItem'],
        (
          items: SeriesStoreItem[] | undefined,
          editingId: string | null | undefined,
          previewSeriesDefinition: PreviewSeriesDefinition | null | undefined,
          previewChartItem: SeriesStoreItem | SeriesStoreItem[] | undefined,
        ): SeriesStoreItem[] => {
          const series = [...(items ?? [])];
          const previewItems = Array.isArray(previewChartItem)
            ? previewChartItem.filter(Boolean)
            : previewChartItem
              ? [previewChartItem]
              : [];
          const displayPreview = !_.isEmpty(previewSeriesDefinition) && previewItems.length > 0;

          if (editingId != null && displayPreview) {
            const itemIndex = series.findIndex((item) => item.id === editingId);
            if (itemIndex >= 0 && previewItems[0]) {
              series.splice(itemIndex, 1, previewItems[0]);
            } else {
              series.push(...previewItems);
            }
          } else if (displayPreview) {
            series.push(...previewItems);
          }

          return series;
        },
      ),
    };
    this.state = this.immutable(initialState);
  }

  get nonCapsuleSeries(): SeriesStoreItem[] {
    return this.getNonCapsuleSeries();
  }

  get primarySeries(): SeriesStoreItem[] {
    return this.state.get('primarySeries') ?? [];
  }

  /**
   * Returns the Id of the series that is being edited.
   */
  get editingId(): string | undefined {
    return this.state.get('editingId');
  }

  /**
   * Returns an Object describing a series (so that it can be updated upon scrolling)
   */
  get previewSeriesDefinition(): PreviewSeriesDefinition | null | undefined {
    return this.state.get('previewSeriesDefinition');
  }

  /**
   * Returns the Store.nonCapusleSeries with the preview added
   */
  get nonCapsuleSeriesAndPreview(): SeriesStoreItem[] {
    return this.addPreview(this.getNonCapsuleSeries());
  }

  /**
   * Returns all the Store.items with the preview added
   */
  get itemsAndPreview(): SeriesStoreItem[] {
    return this.state.get('itemsAndPreview') ?? [];
  }

  /**
   * Returns the preview series
   */
  get previewChartItem(): SeriesStoreItem | SeriesStoreItem[] | undefined {
    return this.state.get('previewChartItem');
  }

  get seriesItems(): SeriesStoreItem[] {
    return (this.state.get('items') as SeriesStoreItem[]) ?? [];
  }

  /**
   * Updates series from uncertain capsules based on the input map of old uncertain capsule ids to new replace
   * capsules. Id, start, end, and duration of capsules will be updated.
   *
   * @param {Object} map - A map of uncertain capsule ids to their replacement capsules
   */
  private syncUncertainCapsuleSeries({
    uncertainReplacements = {},
  }: {
    uncertainReplacements?: Partial<Record<string, CapsuleToAdd>>;
  }): void {
    deriveUncertainCapsuleSeriesRenames(this.seriesItems, uncertainReplacements).forEach(
      ({ seriesId, newSeriesId, newCapsule }) => {
        const index = this.findItemIndex(seriesId);
        if (index > -1) {
          this.state.merge(['items', index], {
            id: newSeriesId,
            capsuleId: newCapsule.id,
            startTime: newCapsule.startTime,
            endTime: newCapsule.endTime,
            duration: getCapsuleDuration(newCapsule.startTime, newCapsule.endTime),
            otherChildrenOf: [newCapsule.isChildOf, newCapsule.id].filter(Boolean),
          });
        }
      },
    );
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    TREND_ADD_CAPSULES: this.syncUncertainCapsuleSeries,
    TREND_ADD_SERIES: this.addSeries,
    TREND_ADD_SERIES_FROM_CAPSULE: this.addSeriesFromCapsule,
    TREND_UNSELECT_ALL_CAPSULES: this.unselectAllCapsuleSeries,
    TREND_REMOVE_SERIES_FROM_CAPSULE: this.removeSeriesFromCapsule,
    TREND_SERIES_RESULTS_SUCCESS: this.addData,
    TREND_MULTIPLE_SERIES_DATA_RESULTS_SUCCESS: this.addSignalSegmentsData,
    TREND_SET_EDITING_SERIES_ID: this.setEditingId,
    TREND_SET_CHART_SERIES_PREVIEW: this.setPreviewChartItem,
    TREND_REMOVE_CHART_SERIES_PREVIEW: this.removePreviewChartItem,
    TREND_REMOVE_ALL_CAPSULE_SERIES: this.removeAllCapsuleSeries,
  };

  /**
   * Adds a series item.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the new item
   * @param {String} payload.name - Name of the new item
   * @param {String} payload.lane - Lane of the new item
   * @param {String} payload.alignment - Alignment of the new item
   * @param {String} [payload.color] - Color hex code (e.g. #CCCCCC)
   */
  private addSeries(payload: AddSeriesPayload): void {
    const interpolationMethodValue =
      typeof payload.interpolationMethod === 'string'
        ? payload.interpolationMethod
        : (payload.interpolationMethod?.value ?? undefined);

    const seriesProps: SeriesProps = {};
    if (payload.color) {
      seriesProps.color = payload.color;
    }
    if (interpolationMethodValue) {
      seriesProps.interpolationMethod = interpolationMethodValue;
    }
    if (payload.laneReservation) {
      seriesProps.laneReservation = payload.laneReservation;
    }

    const series = this.createSeries(payload.id, payload.name, payload.lane, payload.alignment, seriesProps);
    this.state.push('items', series);
  }

  /**
   * Private helper function to add a series to the store using the specified properties.
   *
   * @param {String} id - ID to use for the new series
   * @param {String} name - Name to use for the new series
   * @param {String} lane - Lane to use for the new series
   * @param {String} alignment - Alignment to use for the new series
   * @param {SeriesProps} props - Object containing properties to apply to the new series
   *
   * @returns {Object} Newly created series object.
   */
  private createSeries(id: string, name: string, lane: number, alignment: string, props: SeriesProps): SeriesStoreItem {
    const defaults: SeriesProps = {
      data: [],
      samples: [],
      isStringSeries: false,
      yAxisType: Y_AXIS_TYPES.LINEAR,
      dashStyle: props.dashStyle ?? DASH_STYLES.SOLID,
      lane,
      axisAlign: alignment,
      axisVisibility: props.axisVisibility ?? true,
      axisAutoScale: props.axisAutoScale ?? true,
      yAxisConfig: props.yAxisConfig ?? {},
      lineWidth: props.lineWidth ?? 1,
      sampleDisplayOption: props.sampleDisplayOption ?? SAMPLE_OPTIONS.LINE,
      interpolationMethod:
        typeof props.interpolationMethod === 'string' ? props.interpolationMethod : (props.interpolationMethod ?? ''),
    };

    return createItem(id, name, ITEM_TYPES.SERIES, { ...defaults, ...props }) as SeriesStoreItem;
  }

  /**
   * Creates a series item from a capsule item, and adds the new series item to store. The created
   * series item is created with a new guid and the name property as the capsule name concatenated
   * with the interest (i.e. series) name. Properties from the capsule and other properties copied
   * from the capsule and interest.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object} payload.capsule - Capsule from which to create a series.
   * @param {String} payload.capsule.id - ID of the capsule, to use as the ID of the created series.
   * @param {String} payload.capsule.name - Name of the capsule, to use as the name of the created series.
   * @param {String} payload.capsule.similarity - Profile search capsule property to copy to
   *   the created series.
   * @param {Object} payload.interest - Interest item from the capsule to use as a source for some properties.
   * @param {String} payload.interest.id - ID of the interest.
   */
  private addSeriesFromCapsule(payload: AddSeriesFromCapsulePayload): void {
    const separateByValue =
      payload.capsule.properties?.[sqTrendStore.separateByProperty as keyof CapsuleItem['properties']];
    const colorByValue = payload.capsule.properties?.[sqTrendStore.colorByProperty as keyof CapsuleItem['properties']];

    const resolvedSeparateBy =
      typeof separateByValue === 'string'
        ? separateByValue
        : separateByValue != null
          ? String(separateByValue)
          : undefined;
    const resolvedColorBy =
      typeof colorByValue === 'string' ? colorByValue : colorByValue != null ? String(colorByValue) : undefined;

    const series = this.createSeries(
      createUniqueCapsuleSeriesId(payload.capsule.id, payload.interest.id),
      payload.interest.name ?? '',
      payload.interest.lane ?? 0,
      payload.interest.axisAlign ?? 'left',
      {
        startTime: payload.capsule.startTime ?? undefined,
        // Without this, series with no endTime were displayed “invisibly” on the chart. Hover would show points,
        // but the trend line would not appear. Though we had tried to avoid using this big hammer, in the end it
        // was necessary. CRAB-18916
        endTime: payload.capsule.endTime ?? getAlternateEndTime(),
        duration: payload.capsule.duration,
        capsuleId: payload.capsule.id,
        interestId: payload.interest.id,
        isChildOf: payload.interest.id,
        column: getDefaultCompareViewPropertyValue(resolvedSeparateBy),
        colorBy: getDefaultCompareViewPropertyValue(resolvedColorBy),
        otherChildrenOf: [payload.capsule.isChildOf, payload.capsule.id].filter(Boolean),
        childType: ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE,
        assets: payload.interest.assets,
        selected: Boolean(payload.capsule.selected || payload.interest.selected),
        capsuleSegmentData: [],
        color: payload.interest.color,
        description: payload.interest.description,
        formatOptions: payload.interest.formatOptions,
        sampleDisplayOption: payload.interest.sampleDisplayOption,
        dashStyle: payload.interest.dashStyle,
        axisVisibility: payload.interest.axisVisibility,
        axisAutoScale: payload.interest.axisAutoScale,
        lineWidth: payload.interest.lineWidth,
        rightAxis: payload.interest.rightAxis,
        showDataLabels: payload.interest.showDataLabels,
      },
    );

    this.state.push('items', series);
  }

  /**
   * Handles unselecting all capsuleSeries. This is done because selection status of capsuleSeries is kept in sync
   * with capsules.
   */
  private unselectAllCapsuleSeries() {
    // Every series-from-capsule child unselects, not just the ones the current view shows
    _.forEach(
      this.seriesItems.filter((item) => item.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE),
      (item) => this.setProperty(item.id, 'selected', false),
    );
  }

  /**
   * Removes any series that were added from a capsule item and interest series.
   *
   * @param {Object} payload - Object container for arguments
   * @param payload.seriesFromCapsules - an array of series from capsules to be removed
   */
  private removeSeriesFromCapsule(payload: { capsuleSeriesIds: string[] }) {
    this.removeItems({ items: this.seriesItems.filter((item) => payload.capsuleSeriesIds.includes(item.id)) });
  }

  /**
   * Records what a series fetch reports about the signal itself, and scales the lane to fit the data
   * the fetch produced.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the item the results are for
   * @param {String} payload.valueUnitOfMeasure - Unit of Measure to be displayed
   * @param {String} payload.interpolationMethod - how the samples are interpolated between each other
   */
  private addData(payload: SeriesResultsPayload): void {
    const cursor = this.getItemCursor(payload.id);
    if (!cursor.exists()) {
      return;
    }

    cursor.merge({
      valueUnitOfMeasure: payload.valueUnitOfMeasure ?? '',
      interpolationMethod: payload.interpolationMethod,
    });

    // Ensure that the trend is displayed within its lane when it first appears on the chart
    if (this.addPreview(this.getNonCapsuleSeries()).some((series) => series.id === payload.id)) {
      cursor.merge(updateComputedYAxisExtremes(payload.id));
    }
  }

  /**
   * Records the results of a fetch covering many capsules at once, for the signal and for each of the
   * capsule series the request was built for.
   *
   * @param {Capsule[]} requestCapsules - Information about the capsules used to request data.
   */
  private addSignalSegmentsData({
    id,
    requestCapsules,
    valueUnitOfMeasure,
    interpolationMethod,
  }: SignalSegmentsPayload): void {
    const results = { valueUnitOfMeasure, interpolationMethod };
    this.addData({ id, ...results });

    requestCapsules.forEach(({ capsuleSeriesId }) => {
      if (capsuleSeriesId) {
        this.addData({ id: capsuleSeriesId, ...results });
      }
    });
  }

  /**
   * Sets the id of the Series being edited.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - the id.
   */
  private setEditingId(payload: { id: string | undefined }): void {
    this.state.set('editingId', payload.id);
    this.state.set('previewSeriesDefinition', null);
  }

  /**
   * Sets the preview series so they can be displayed. Also stores an Object that defines how to generate those
   * preview series so they can be updated when the users scrolls.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.formula - formula used to create the series
   * @param {Object[]} payload.parameters - Object array describing the parameters used to create the series
   * @param {String} payload.id - ID of the existing item
   * @param {String} payload.name - Name to use for the new series
   * @param {Object[]} payload.data - Array of data points
   * @param {Number} payload.samples[].key - x-value of a data point
   * @param {Number} payload.samples[].value - y-value of a data point
   * @param {Boolean} payload.samples[].isUncertain - true if this data point is uncertain
   * @param {String} payload.valueUnitOfMeasure - Unit of Measure to be displayed
   * @param {String} payload.lane - Lane of the new item
   * @param {String} payload.alignment - Alignment of the new item
   * @param {String} payload.color - Color hex code (e.g. #CCCCCC)
   */
  private setPreviewChartItem(payload: PreviewSeriesPayload): void {
    const previewId = getPreviewSeriesId(payload.id);
    const sampleDisplayOption = payload.sampleDisplayOption ?? SAMPLE_OPTIONS.LINE;

    if (!payload.samples || payload.samples.length === 0) {
      this.state.set('previewSeriesDefinition', undefined);
      this.state.set('previewChartItem', undefined);
      return;
    }

    const previewDefinition: PreviewSeriesDefinition = {
      formula: payload.formula,
      parameters: payload.parameters,
      id: previewId,
      color: payload.color ?? undefined,
      sampleDisplayOption,
    };
    this.state.set('previewSeriesDefinition', previewDefinition);

    const previewItem = this.createSeries(previewId, payload.name, payload.lane, payload.alignment, {
      color: payload.color ?? undefined,
      lineWidth: payload.lineWidth,
      valueUnitOfMeasure: payload.valueUnitOfMeasure ?? '',
      interpolationMethod: payload.interpolationMethod,
    });

    this.state.set('previewChartItem', previewItem);
    this.state.merge('previewChartItem', getYAxisConfig(previewId) as Record<string, unknown>);
  }

  /**
   * Removes the previewChartItem.
   */
  private removePreviewChartItem() {
    const previewChartItem = this.state.get('previewChartItem') as SeriesStoreItem | undefined;
    if (previewChartItem) {
      if (typeof previewChartItem.color === 'string') {
        removeColor(previewChartItem.color);
      }
      this.state.merge({
        previewChartItem: undefined,
        previewSeriesDefinition: undefined,
      });
    }
  }

  /**
   * Handles removing all capsuleSeries.
   */
  private removeAllCapsuleSeries(payload: { capsuleSeriesIds: string[] }) {
    this.removeItems({ items: this.seriesItems.filter((item) => payload.capsuleSeriesIds.includes(item.id)) });
  }

  /**
   * Returns all series that are not converted from capsules
   */
  private getNonCapsuleSeries() {
    return this.seriesItems.filter(
      (item) => !item.isChildOf || item.childType !== ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE,
    );
  }

  /**
   * This function manages the correct inclusion of preview series. If a new Search is performed then the
   * resulting preview series are simply appended. If an existing search is edited then the original series are
   * omitted and replaced with the preview series.
   */
  private addPreview(items: SeriesStoreItem[]): SeriesStoreItem[] {
    const series = [...items];
    const editingId = this.state.get('editingId') as string | null | undefined;
    const previewSeriesDef = this.state.get('previewSeriesDefinition') as PreviewSeriesDefinition | null | undefined;
    const previewChartItem = this.state.get('previewChartItem') as SeriesStoreItem | SeriesStoreItem[] | undefined;
    const previewItems = Array.isArray(previewChartItem)
      ? previewChartItem.filter(Boolean)
      : previewChartItem
        ? [previewChartItem]
        : [];
    const displayPreview = !_.isEmpty(previewSeriesDef) && previewItems.length > 0;

    if (editingId != null && displayPreview) {
      const previewReplacement = previewItems[0];
      const itemIndex = series.findIndex((item) => item.id === editingId);
      if (itemIndex >= 0 && previewReplacement) {
        series.splice(itemIndex, 1, previewReplacement);
      } else {
        series.push(...previewItems);
      }
    } else if (displayPreview) {
      series.push(...previewItems);
    }

    return series;
  }

  /**
   * Overrides the behavior of TREND_SET_COLOR so a series from a capsule keeps the color of the signal
   * it came from rather than the color of the condition that produced the capsule
   *
   * Called when the color for an item changes, should set the color on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} color - color that should be set
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetColor(item: Record<string, unknown> | undefined, color: string, parentId?: string): void {
    const series = item as SeriesStoreItem | undefined;
    if (parentId && series?.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE && parentId !== series.interestId) {
      return; // Colors for signal from capsule come from the signal, not the condition
    }

    if (!series) {
      return;
    }

    this.getItemCursor(series.id).set('color', color);
  }

  /**
   * Overrides the behavior of TREND_SET_SELECTED so that selection from capsules determine the selection of
   * series from capsules
   *
   * Called when the selection for an item change, should set the color on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} selected - selection status
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetSelected(item: Record<string, unknown> | undefined, selected: boolean, parentId?: string): void {
    const series = item as SeriesStoreItem | undefined;
    if (
      parentId &&
      series?.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE &&
      parentId !== series.capsuleId &&
      parentId !== series.interestId
    ) {
      return; // Selection status for series from capsule should only come from its capsule or interest signal.
    }

    if (!series) {
      return;
    }

    this.state.select('items', this.findItemIndex(series.id)).set('selected', selected);
    if (this.isPreviewItem(series.id)) {
      this.state.select('previewChartItem').set('selected', selected);
    }
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): TrendSeriesDehydratedState {
    const extraPropsToDehydrate = ['displayedAncillaryItemIds', 'interpolationMethod'];

    return {
      items: _.chain(this.seriesItems)
        .filter(shouldDehydrateItem)
        .map((item) => pruneDehydratedItemProps(item, extraPropsToDehydrate))
        .value(),
      editingId: this.state.get('editingId') as string | null | undefined,
      previewSeriesDefinition: this.state.get('previewSeriesDefinition') as PreviewSeriesDefinition | null | undefined,
    };
  }

  /**
   * Re-creates the series. Calls the action to rehydrate the data points.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   *
   * @return {Object} A promise that is fulfilled when items are completely rehydrated.
   */
  rehydrate(dehydratedState: TrendSeriesDehydratedState): void {
    this.state.set(
      'items',
      dehydratedState.items.map((item) =>
        this.createSeries(
          item.id,
          item.name ?? '',
          item.lane ?? 0,
          (item.axisAlign as string | undefined) ?? (item.alignment as string | undefined) ?? 'left',
          _.omit(item, ['id', 'name', 'lane', 'alignment']) as SeriesProps,
        ),
      ),
    );

    this.state.set('editingId', dehydratedState.editingId);
    this.state.set('previewSeriesDefinition', dehydratedState.previewSeriesDefinition);
  }
}
